import React, { useState } from 'react';
import { FiSettings as Settings, FiX as X, FiRefreshCw as RefreshCw, FiMenu as GripHorizontal } from 'react-icons/fi';
import EmptyWidgetState from './EmptyWidgetState';
import { getEmptyStateForWidget, WIDGET_SIZES } from './widgetRegistry';

interface WidgetDefinition {
  id: string;
  name: string;
  icon?: React.ComponentType<{ className?: string }>;
  defaultSize?: unknown;
  [key: string]: unknown;
}

interface EmptyStateConfig {
  title?: string;
  instructions?: string[];
  docsUrl?: string;
  actionLabel?: string;
  actionPath?: string;
}

interface Props {
  widget: WidgetDefinition;
  children?: React.ReactNode;
  onRemove?: () => void;
  onConfigure?: () => void;
  editMode?: boolean;
  onRefresh?: () => void;
  isEmpty?: boolean;
  isLoading?: boolean;
}

/**
 * WidgetWrapper - Container for dashboard widgets
 * Handles empty states, loading states, and widget chrome
 */
const WidgetWrapper: React.FC<Props> = ({
  widget,
  children,
  onRemove,
  onConfigure,
  editMode,
  onRefresh,
  isEmpty = false,
  isLoading = false
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const isSmallWidget = widget.defaultSize === WIDGET_SIZES.SMALL;
  const emptyStateConfig: EmptyStateConfig | null = getEmptyStateForWidget(widget.id);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    if (isEmpty && emptyStateConfig) {
      return (
        <EmptyWidgetState
          title={emptyStateConfig.title}
          instructions={emptyStateConfig.instructions}
          docsUrl={emptyStateConfig.docsUrl}
          actionLabel={emptyStateConfig.actionLabel}
          actionPath={emptyStateConfig.actionPath}
          compact={isSmallWidget}
        />
      );
    }

    return children;
  };

  const WidgetIcon = widget.icon;

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-lg hover:shadow-xl dark:shadow-cyan-500/5 dark:hover:shadow-cyan-500/10 border border-gray-100 dark:border-gray-700/50 hover:border-gray-200 dark:hover:border-cyan-500/30 transition-all duration-300 relative h-full flex flex-col"
      role="presentation"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Widget Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className={`flex items-center gap-2 flex-1 ${editMode ? 'cursor-move' : ''}`}>
          {editMode && (
            <GripHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          )}
          {WidgetIcon && (
            <WidgetIcon className="w-5 h-5 text-blue-500 dark:text-blue-400" />
          )}
          <h3 className="text-sm font-medium text-gray-900 dark:text-white transition-colors">
            {widget.name}
          </h3>
        </div>

        <div className={`flex items-center gap-2 transition-opacity ${isHovered || editMode ? 'opacity-100' : 'opacity-0'}`}>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          )}
          {onConfigure && (
            <button
              onClick={onConfigure}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Configure"
            >
              <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
          )}
          {editMode && onRemove && (
            <button
              onClick={onRemove}
              className="p-1 hover:bg-red-100 dark:hover:bg-red-900/50 rounded transition-colors"
              title="Remove"
            >
              <X className="w-4 h-4 text-red-500 dark:text-red-400" />
            </button>
          )}
        </div>
      </div>

      {/* Widget Content */}
      <div className="flex-1 p-4 overflow-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default WidgetWrapper;
